import { NetworkStatus } from '@apollo/client';
import { Box, IconSize, useModalState } from '@bechtel/gds';
import { DataType, MetadataColumn, MetadataTableName, RequiredData, UserAccountLayout } from '@bechtel/sr-types';
import {
  CellClassParams,
  CellEditingStoppedEvent,
  CellKeyDownEvent,
  ColumnState,
  ProcessCellForExportParams,
  RowDragMoveEvent,
  ValueFormatterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { isEqual as isDateEqual } from 'date-fns';
import {
  difference,
  filter,
  find,
  first,
  forEach,
  get,
  has,
  includes,
  isEmpty,
  isEqual,
  isNil,
  keys,
  last,
  map,
  pickBy,
  remove,
  replace,
  sortBy,
  split,
  upperCase,
} from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  AppSpinner,
  CustomCellEditor,
  ErrorRetry,
  MultiLingualText,
  TableMetadataContext,
  useAppUser,
  useLanguage,
} from '~/components';
import { useEntityAffectedSubscription, useLocalStorage } from '~/hooks';
import { cellRendererComponents, getCellRenderers } from '../cell-renderers';
import { FloatingFilter } from '../custom-filters/text-num-filter';
import {
  useDefaultColumnDefs,
  useDefaultLayout,
  useDefaultSortColumns,
  useDragColumnChange,
  useFetchTableMetadata,
  useMetadataColumns,
  useStatusBar,
} from '../hooks';
import { LayoutModal } from '../layout-modal';
import { GridWrapperStyled } from '../styled';
import { ClientGridProps, DEFAULT_FONT_SIZE, DEFAULT_ROW_HEIGHT, sideBarData } from '../types';
import {
  actionColumn,
  checkBoxColumn,
  checkBoxColumnDefs,
  getCellDataType,
  getColumnWidth,
  getDisplayColumns,
  getDisplayFieldName,
  getDisplayedSelectClause,
  getFormattedFilters,
  getHeaderHeight,
  getIssuedCommodities,
  getSortState,
  getUpdatedColumnDefs,
  hasFilterOrSortChanged,
  isColumnEditable,
  toUpperString,
  updateObjectByKey,
} from '../utils';
import { ClientGridHeader } from './grid-header';

export const ClientGrid = <T extends object & { originalValue?: T }>({
  name,
  rowData,
  originalRowData,
  rowSelection = 'single',
  systemLayoutName,
  rowHeight,
  fontSize,
  customColDefs,
  navigationURL,
  onClick,
  onDoubleClick,
  onRowEdited,
  onRowsDeleted,
  rowDraggableColumn,
  rowDragManaged = false,
  onRowDragged: onRowMoved,
  validationFns,
  showTotal = false,
  readOnly,
  onValidate,
  skipPermission,
  disableNewRow = false,
  mixedCaseFilter = false,
  updateSubscriptionEntity,
  showHeader = false,
  autoSizeStrategy,
  onGridInitialize,
  onGridRowSelected,
  picklistKeys = null,
  hideSaveLayout = false,
  isChangeControl = false,
  changeManagementPermissions,
  changeManagementSignature,
  engineeringStatus,
  canAddItem = true,
  canDeleteItem = true,
  uniqueIdentifier = 'tag',
  showActionColumn = false,
  hideSearch = false,
  customDefaultColDef = null,
  onSortChange,
  showSidebar = false,
  hideFloatingFilter = false,
  headerHeight,
  containerHorizontalSpacing = '450',
  insertRow,
  actionColumnPosition,
  tableMetaData,
  hideCheckBoxColumn = false,
  sortable = false,
}: ClientGridProps<T> & { children?: ReactNode }) => {
  const gridRef = useRef<AgGridReact>(null);
  const theme = useTheme();
  const { isAlternateLanguageUsed } = useLanguage();
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const appUser = useAppUser();
  const { hasPermission } = useAppUser();
  const entityInfo = useEntityAffectedSubscription();
  const [flash, setFlash] = useState<boolean>(false);
  const [erroredRowIds, setErroredRowIds] = useState<string[]>();
  const { close: closeLayoutModal, open: saveLayoutModal, visible: layoutModalVisible } = useModalState(false);
  const filterModel = gridRef.current?.api?.getFilterModel();
  const { loading, error, data, refetch, networkStatus } = useFetchTableMetadata({ name, appUser, tableMetaData });
  const defaultSortColumns = useDefaultSortColumns({ data });
  const isDirty: boolean = !isEqual(rowData, originalRowData);

  useEffect(() => {
    if (gridRef?.current?.api && onGridInitialize) {
      onGridInitialize(gridRef.current.api);
    }
  }, [onGridInitialize]);

  // subscription related changes
  const exceptionalNames = useMemo(() => {
    return [MetadataTableName.FIRE_AREA, MetadataTableName.COLOR_CODE];
  }, []);

  const getEntityItems = (entityInfo) => {
    if (entityInfo?.name === MetadataTableName.FIRE_AREA) return entityInfo?.items[0].fireAreaZones;
    if (entityInfo?.name === MetadataTableName.COLOR_CODE) return entityInfo?.items[0].colorCodeColors;
    else return entityInfo?.items;
  };

  useEffect(() => {
    if (
      !entityInfo ||
      !entityInfo.name ||
      !entityInfo.items ||
      (entityInfo.name !== name && !includes(exceptionalNames, entityInfo?.name))
    )
      return;

    const entityItems = getEntityItems(entityInfo);

    setFlash(true);
    forEach(entityItems, (item) => {
      gridRef?.current?.api.forEachNode((rowNode) => {
        if (rowNode.data.id === item.id) {
          setTimeout(() => {
            rowNode.updateData({ ...rowNode.data, ...item });
            setFlash(false);
          }, 10);
        }
      });
    });

    updateSubscriptionEntity && updateSubscriptionEntity(entityInfo);
  }, [exceptionalNames, entityInfo, name, updateSubscriptionEntity]);

  const metadataColumns = useMetadataColumns(data);
  const defaultLayout = useDefaultLayout({ data, metadataColumns, systemLayoutName });
  const allLayouts = useMemo(
    () => [defaultLayout, ...get(data, 'tableMetadata.userAccountLayouts', [])],
    [data, defaultLayout]
  );
  const localStorageKey = `${appUser?.userAccount?.loginName}_${systemLayoutName}`;
  const [selectedLayoutId, setSelectedLayoutId] = useLocalStorage(localStorageKey, defaultLayout.id);
  const [selectedColumns, setSelectedColumns] = useState<string | undefined>(undefined);

  const [layoutVisibleFlag, setLayoutVisibleFlag] = useState(false);
  const [sortingOrder, setSortingOrder] = useState<ColumnState[]>([]);

  const selectedLayout: UserAccountLayout = useMemo(() => {
    const foundLayout = selectedLayoutId
      ? find(allLayouts, (userAccountLayout) => userAccountLayout.id === selectedLayoutId)
      : null;

    return foundLayout ? foundLayout : defaultLayout;
  }, [allLayouts, defaultLayout, selectedLayoutId]);
  // Array of colIds that are hidden using customColDefs
  const customColDefHiddenCols = keys(pickBy(customColDefs, (value) => get(value, 'hide') === true));
  //  removing the whitespaces from selectClause
  const selectClauseTrimmed = replace(selectedLayout?.selectClause ?? '', /\s+/g, '');
  // removing the hidden columns (using customColDefs) from selectClause.
  // Because even though we have those columns in the selectClause, we are not showing them
  const selectClause = difference(selectClauseTrimmed?.split(','), customColDefHiddenCols).join(',');
  const displayedSelectClause = getDisplayedSelectClause(gridRef);
  const statusBar = useStatusBar({ rowHeight, isServerGrid: false });
  const defaultColumnDefs = useDefaultColumnDefs(true, customDefaultColDef, isDirty);

  const { onDragStarted, onDragStopped } = useDragColumnChange(
    setSelectedColumns,
    selectedLayout,
    sortingOrder,
    selectClause,
    setLayoutVisibleFlag,
    defaultLayout.layoutName === selectedLayout.layoutName,
    sortable
  );

  useEffect(() => {
    gridRef.current?.api?.setFilterModel(getFormattedFilters(selectedLayout?.whereClause));
  }, [selectedLayout?.whereClause]);

  const getCellStyle = useCallback(
    (column: MetadataColumn, params: CellClassParams, field: string) => {
      //  Avoid checking permissions if skipPermission is passed
      const hasFieldPermission =
        column && (hasPermission(`${data?.tableMetadata?.name}.${column?.name}`) || skipPermission);
      const customColDef = (customColDefs && customColDefs[column.memberName]) || {};
      const fieldValue = get(params.data, field) ?? null;

      const originalValue = get(
        originalRowData?.find((item) => item['id'] === params.data.id),
        field
      );
      // user might not have the field permission, in that case the column is not editable.
      const metadataColumnEditable = hasFieldPermission || skipPermission ? column?.isUserEditable : false;

      const customEditableCallback =
        typeof customColDef.editable === 'boolean'
          ? customColDef.editable
          : customColDef?.editable && customColDef?.editable(params);

      // If customColDef has editable property, grey color should be displayed based on that, else the normal conditions
      const userEditable = has(customColDef, 'editable') ? customEditableCallback : !readOnly && metadataColumnEditable;
      const customPreparedCallback =
        typeof customColDef.prepared === 'boolean'
          ? customColDef.prepared
          : customColDef?.prepared && customColDef?.prepared(params);
      const prepared = has(customColDef, 'prepared')
        ? customPreparedCallback
        : isChangeControl && Number(engineeringStatus) > 0;
      const isLastRow = params.rowIndex === rowData?.length && !params.data?.id;
      if (isChangeControl && prepared) {
        return { backgroundColor: theme.colors.yellow[100] };
      }
      if (!userEditable) {
        return { backgroundColor: theme.colors.grey[100] };
      }

      const isValueEmpty = includes([DataType.NUMBER, DataType.BOOLEAN], column?.dataType)
        ? isNil(fieldValue)
        : isEmpty(fieldValue);
      // Along with the lastRow, red cell validation should not be applied to those cells which are related member because related member cells will be auto filled(not editable by user)
      if (isValueEmpty && column.requiredData && !isLastRow && !column.relatedMemberName) {
        return { backgroundColor: theme.colors.red[100] };
      }
      // equality check for date/time. Date objects should be compared properly (Date-fns isEqual method used)
      const isDateColumn = includes([DataType.DATE, DataType.DATETIME], column.dataType);
      if (
        isDateColumn &&
        !isDateEqual(new Date(fieldValue), new Date(originalValue)) &&
        params.rowIndex !== rowData?.length
      ) {
        return { backgroundColor: theme.colors.blue[100] };
      }
      // equality check for datatype other than date/time.
      if (!isDateColumn && fieldValue !== originalValue && params.rowIndex !== rowData?.length) {
        return { backgroundColor: theme.colors.blue[100] };
      }
      // revert to its default background color
      return { backgroundColor: 'initial' };
    },
    [
      hasPermission,
      data?.tableMetadata?.name,
      customColDefs,
      engineeringStatus,
      isChangeControl,
      originalRowData,
      readOnly,
      rowData?.length,
      skipPermission,
      theme,
    ]
  );

  const hasColumnStateChanged = useCallback(() => {
    const displayedColumns = map(gridRef?.current?.api?.getAllDisplayedColumns(), (column) => column.getColDef().colId);
    // colId == "checkbox-column" will be added for first checkbox colunm so here filtering that column out for compare
    const displayedColumnsExceptCheckbox = filter(displayedColumns, (item) => item != checkBoxColumn);
    const currentColumnState = replace(displayedColumnsExceptCheckbox.join(','), /\s+/g, '');
    return currentColumnState !== selectClause.replace(`${checkBoxColumn},`, '');
  }, [selectClause]);

  const onFilterChanged = useCallback(() => {
    const sortOrFilterChanged = hasFilterOrSortChanged(
      selectedLayout,
      defaultLayout.layoutName === selectedLayout.layoutName,
      sortable,
      gridRef?.current?.api?.getFilterModel(),
      sortingOrder
    );
    setLayoutVisibleFlag(sortOrFilterChanged || hasColumnStateChanged());
  }, [defaultLayout.layoutName, hasColumnStateChanged, selectedLayout, sortable, sortingOrder]);

  const onSortChanged = useCallback(() => {
    const sortState = getSortState(
      gridRef.current?.api?.getColumnState() ? gridRef.current?.api?.getColumnState() : []
    );
    if (sortState) setSortingOrder(sortState);
    const sortOrFilterChanged = hasFilterOrSortChanged(
      selectedLayout,
      defaultLayout.layoutName === selectedLayout.layoutName,
      sortable,
      gridRef?.current?.api?.getFilterModel(),
      sortState
    );
    setLayoutVisibleFlag(sortOrFilterChanged || hasColumnStateChanged());
  }, [defaultLayout.layoutName, hasColumnStateChanged, selectedLayout, sortable]);

  const onColumnVisibilityChanged = useCallback(() => {
    const sortState = getSortState(
      gridRef.current?.api?.getColumnState() ? gridRef.current?.api?.getColumnState() : []
    );
    const sortOrFilterApplied = hasFilterOrSortChanged(
      selectedLayout,
      defaultLayout.layoutName === selectedLayout.layoutName,
      sortable,
      gridRef?.current?.api?.getFilterModel(),
      sortState
    );

    if (!hasColumnStateChanged() && !sortOrFilterApplied) {
      setLayoutVisibleFlag(false);
    } else {
      const currentColumnState = map(
        gridRef?.current?.api?.getAllDisplayedColumns(),
        (column) => column.getColDef().colId
      ).join(',');
      setLayoutVisibleFlag(true);
      setSelectedColumns(currentColumnState);
    }
  }, [defaultLayout.layoutName, hasColumnStateChanged, selectedLayout, sortable]);

  const onLayoutChanged = (layout) => {
    setSelectedLayoutId(layout.id);
  };

  const columnDefs: ColDef<T>[] = useMemo(() => {
    const displayColumns = getDisplayColumns(metadataColumns);
    //The first sort field should be pinned, non-draggable and  cannot be hidden or removed from the layout.
    const indexColumn = defaultSortColumns?.[0].colId;

    // Based on systemAccountLayout, we are modifying the displayColumns
    // columnDef will be formed for only the columns present in systemAccountLayout's selectClause
    // Handling the systemAccountLayout here will help the checkboxSelection to get correct indexes in the params.
    const systemAccountLayout = systemLayoutName
      ? find(data?.tableMetadata?.systemAccountLayouts, (systemAccountLayout: UserAccountLayout) => {
          return systemAccountLayout.layoutName === systemLayoutName;
        })
      : null;
    const systemLayoutColumns = systemLayoutName ? split(get(systemAccountLayout, 'selectClause'), ', ') : null;

    //arrange the columnDef as per systemLayout definition
    const sortedColumnDefs = sortBy(displayColumns, (metadataColumn: MetadataColumn) => {
      if (systemLayoutColumns?.length) {
        const columnIndex = systemLayoutColumns?.indexOf(metadataColumn?.memberName);
        //If columnIndex = -1, it means that column is not part of the system account layout, so they go to the last in the list to show in the column selection
        return columnIndex === -1 ? systemLayoutColumns?.length + metadataColumn.ordinalPosition : columnIndex;
      } else {
        return metadataColumn.ordinalPosition;
      }
    });
    if (showActionColumn) {
      const actionColumnDef = { ...actionColumn, projectId: appUser.userAccount?.defaultProjectId ?? '' };
      if (actionColumnPosition) {
        sortedColumnDefs.splice(actionColumnPosition, 0, actionColumnDef);
      } else {
        sortedColumnDefs.push(actionColumnDef);
      }
    }

    const displayColumnDefs: ColDef[] = sortedColumnDefs.map((column: MetadataColumn, index: number) => {
      const fieldName = getDisplayFieldName(column);
      const customColDef = customColDefs && customColDefs[column.memberName];
      //If system account layout name is provided but the column is not available in it, then hide it
      const isActionColumn = column.memberName === 'actionColumn';
      const hideField =
        (systemLayoutColumns && !includes(systemLayoutColumns, column.memberName) && !isActionColumn) || false;
      const columnHeading = !isAlternateLanguageUsed ? column.columnHeading : column.alternateColumnHeading;
      const hasFieldPermission = column && hasPermission(`${data?.tableMetadata?.name}.${column?.name}`);
      const isColEditable =
        customColDef && typeof customColDef?.editable === 'boolean'
          ? customColDef.editable
          : isColumnEditable(false, column, hasFieldPermission, isChangeControl, readOnly, skipPermission, undefined);
      const isRequired =
        isColEditable &&
        (includes([RequiredData.YES, RequiredData.ALWAYS], column.requiredData) || customColDef?.required);
      return {
        colId: column.memberName,
        field: fieldName,
        sortable: sortable,
        // adding the has check since params.data[rowDraggableColumn] was added for the new empty row.
        // Empty row will not have any keys. So this will work.
        // Also in case the tag is removed, we still want to show the drag icon
        rowDrag: (params) =>
          // drag only if the grid is editable
          (!readOnly || rowDragManaged) &&
          params.data &&
          rowDraggableColumn &&
          has(params.data, rowDraggableColumn) &&
          rowDraggableColumn === fieldName,
        suppressMovable: indexColumn === fieldName || rowDraggableColumn === fieldName,
        lockVisible: indexColumn === fieldName,
        pinned: indexColumn === fieldName && 'left',
        headerName: columnHeading?.replaceAll(';', '\n') ?? fieldName,
        headerClass: isRequired ? 'is-required' : '',
        width: getColumnWidth(column.columnWidth || 0, columnHeading),
        suppressColumnsToolPanel: column.dataType === DataType.GUID && !column.relatedMemberName ? true : false,
        hide: (column.dataType === DataType.GUID && !column.relatedMemberName) || hideField, //Hide id field
        filter: !hideFloatingFilter && !isActionColumn && 'agMultiColumnFilter',
        floatingFilterComponent: includes([DataType.NUMBER, DataType.STRING], column.dataType)
          ? (props) => {
              // anytime the layout is changed, Floating filter can be re-rendered
              return <FloatingFilter key={selectedLayoutId} {...props} dataType={column.dataType} />;
            }
          : undefined,
        floatingFilter: hideFloatingFilter ? false : true,
        filterParams: {
          filters: [
            {
              filter: column.dataType === DataType.NUMBER ? 'agNumberColumnFilter' : 'agTextColumnFilter',
            },
          ],
        },
        cellStyle: (params) => {
          return getCellStyle(column, params, fieldName);
        },
        editable: isColumnEditable(
          false,
          column,
          hasFieldPermission,
          isChangeControl,
          readOnly,
          skipPermission,
          undefined
        ),
        valueFormatter: (params: ValueFormatterParams) => {
          if (column.dataType === DataType.NUMBER && column.decimals !== 0) {
            const fieldData = isNaN(get(params.data, fieldName)) ? '' : Number(get(params.data, fieldName));
            return typeof fieldData == 'number' ? fieldData?.toFixed(column.decimals) : fieldData;
          }
          // passing this to avoid the typescript error
          return params.value;
        },
        valueSetter: (params: ValueSetterParams) => {
          const newData = {
            ...params.data,
            [fieldName]:
              // only change alphabets into uppercase. Otherwise it will add a space between characters (numbers and alphabets).
              includes([DataType.STRING, DataType.GUID], column.dataType) &&
              (!mixedCaseFilter || customColDef?.isUpperCase)
                ? params?.newValue?.replace(/[a-zA-Z]+/g, upperCase) ?? null
                : params?.newValue ?? null,
          };
          params?.node?.setData(newData);
          return true;
        },
        cellRenderer: getCellRenderers(index, column.dataType, column.pickListName),
        type: column.dataType === DataType.NUMBER ? 'numericColumn' : '',
        // to override default cell editor and make use of custom cell editor with picklist button
        cellEditor:
          !column.pickListName && column.dataType === DataType.NUMBER
            ? 'agNumberCellEditor'
            : column.pickListName && CustomCellEditor,
        // params for custom cell editor. we created custom editor to show picklist button along with cell editor
        cellEditorParams: {
          onRowEdited: onRowEdited,
          rowHeight: rowHeight ?? DEFAULT_ROW_HEIGHT,
          maxLength: column.columnWidth, //to set max length limit to string cells
          validationFns,
          picklistKeys: picklistKeys ?? {},
        },
        headerCheckboxSelectionFilteredOnly: true,
        cellDataType: getCellDataType(column.dataType),
        cellClass: customColDef?.isUpperCase ? 'uppercase-cell' : '',
        ...customColDef,
      };
    });

    return displayColumnDefs;
  }, [
    metadataColumns,
    defaultSortColumns,
    systemLayoutName,
    data?.tableMetadata?.systemAccountLayouts,
    data?.tableMetadata?.name,
    showActionColumn,
    appUser.userAccount?.defaultProjectId,
    actionColumnPosition,
    customColDefs,
    isAlternateLanguageUsed,
    hasPermission,
    isChangeControl,
    readOnly,
    skipPermission,
    sortable,
    rowDraggableColumn,
    hideFloatingFilter,
    onRowEdited,
    rowHeight,
    validationFns,
    picklistKeys,
    rowDragManaged,
    selectedLayoutId,
    getCellStyle,
    mixedCaseFilter,
  ]);

  const userDefinedColumnDefs = useMemo(() => {
    const userSelectedLayoutColumns = split(get(selectedLayout, 'selectClause', ''), ', ');
    const updatedColumnDefs = getUpdatedColumnDefs(columnDefs, userSelectedLayoutColumns);
    const filteredAndReorderedColumns = selectedLayout.id !== defaultLayout.id ? updatedColumnDefs : columnDefs;

    return filteredAndReorderedColumns;
  }, [columnDefs, defaultLayout.id, selectedLayout]);

  const gridColumnDefs = useMemo(() => {
    const allColumnDefs = selectedLayout.id == defaultLayout.id ? columnDefs : userDefinedColumnDefs;
    return hideCheckBoxColumn ? allColumnDefs : [checkBoxColumnDefs, ...allColumnDefs];
  }, [columnDefs, defaultLayout, selectedLayout, userDefinedColumnDefs, hideCheckBoxColumn]);

  //function to add empty row at the end of the grid.
  const addNewRow = useCallback(() => {
    if (disableNewRow) return;
    if (gridRef.current?.api) {
      setTimeout(() => {
        gridRef.current?.api.applyTransaction({
          remove: [{}],
        });
        gridRef.current?.api.applyTransaction({
          add: [{}],
        });
      }, 100);
      // }
    }
    // start editing the first cell of the new row
  }, [disableNewRow]);

  useEffect(() => {
    gridRef.current?.api && addNewRow();
  }, [addNewRow, rowData]);

  const handleGridReady = useCallback(
    (params) => {
      // This function will be called when the grid is ready
      const { api } = params;
      if (api) {
        if (!disableNewRow) {
          //  add new row on initial load
          addNewRow();
        }

        if (selectedLayout?.whereClause || selectedLayout?.orderByClause) {
          gridRef.current?.api?.setFilterModel(getFormattedFilters(selectedLayout?.whereClause));
        }
        if (onGridInitialize) {
          onGridInitialize(api, name);
        }
      }
    },
    [addNewRow, disableNewRow, name, onGridInitialize, selectedLayout?.orderByClause, selectedLayout?.whereClause]
  );

  useEffect(() => {
    if (onValidate) {
      onValidate(erroredRowIds?.length ? true : false);
    }
  }, [erroredRowIds, onValidate]);

  const onCellEditingStopped = useCallback(
    async (params: ProcessCellForExportParams & CellEditingStoppedEvent<T>) => {
      const {
        column,
        node: { data },
      } = params;

      //For typescript type checking
      if (!data) return;

      const colId = column?.getColId();
      const colDef = column?.getColDef();
      const metadataColumn = find(metadataColumns, (item) => item.memberName == colId);
      let newValue = params?.newValue ?? null;
      let oldValue = params?.oldValue ?? null;
      const isClipboardAction = !has(params, 'newValue');
      // if cell is edited then oldValue/newValue is received from params or if newValue and oldValue is not present in params then it is clipboard action
      if (isClipboardAction) {
        newValue = isEmpty(params.value) ? null : toUpperString(params.value, metadataColumn) ?? null;

        oldValue = metadataColumn?.relatedMemberName
          ? get(data, `${metadataColumn?.relatedMemberName}.${metadataColumn?.relatedMemberDisplayField}`)
          : get(data, colId);
      }

      // isNil only works for number and boolean and not for other datatypes.
      const isValueEmpty = includes([DataType.NUMBER, DataType.BOOLEAN], metadataColumn?.dataType) ? isNil : isEmpty;
      //if the old value and new value is not changed, it means cell editing did not really happen
      //tabbing out of client grid cell causes onCellEditingStopped to be called twice when a new row may be created and focus set on it
      // checking isValueEmpty because the oldValue can be null and newValue can be empty string. In this case also the value has not really change.
      // isClipboardAction check is added because on copy paste twice the data will be removed.
      if ((oldValue === newValue && !isClipboardAction) || (isValueEmpty(oldValue) && isValueEmpty(newValue))) {
        return;
      }

      const validateColData = validationFns?.[colId];

      // if datatype is number and newvalue is not in range then revert to oldvalue
      if (
        metadataColumn?.dataType === DataType.NUMBER &&
        (newValue < metadataColumn.minValue || newValue > metadataColumn.maxValue || isNil(newValue))
      ) {
        if (isClipboardAction) {
          params?.node?.setData({
            ...data,
            [colId]: oldValue,
          });
          newValue = oldValue;
        } else {
          data[colId] = oldValue;
        }
        gridRef.current?.api.redrawRows({ rowNodes: [params.node] });
      }

      if (validateColData) {
        // passing params to get the grid data in the validationFns
        const result = await validateColData(newValue, data);
        const originalValue = get(
          originalRowData?.find((item) => item['id'] === data['id']),
          colId
        );

        if (!result) {
          // Reverting back to the previous value if incorrect value is entered || validation fails
          if (isClipboardAction) {
            params?.node?.setData({
              ...data,
              [colId]: oldValue,
            });
          } else {
            data[colId] = oldValue;
          }
          gridRef.current?.api.redrawRows({ rowNodes: [params.node] });
          if (oldValue !== originalValue && !(isValueEmpty(oldValue) && isValueEmpty(originalValue))) {
            colDef.cellStyle = { backgroundColor: theme.colors.blue[100] };
            gridRef.current?.api.redrawRows({ rowNodes: [params.node] });
          } else {
            colDef.cellStyle = { backgroundColor: 'initial' };
            gridRef.current?.api.redrawRows({ rowNodes: [params.node] });
          }
          if (!includes(erroredRowIds, data['id'])) {
            setErroredRowIds(erroredRowIds?.length ? [...erroredRowIds, data['id']] : [data['id']]);
          }
          return;
        } else {
          if (erroredRowIds?.length) {
            setErroredRowIds([...remove(erroredRowIds, data['id'])]);
          }
        }
      }

      if (onRowEdited && rowData) {
        const index = rowData.findIndex((item: T) => item['id'] === data['id']);
        /*
         while adding data in new row
          index === -1 says that record not present in rowData
          params.rowIndex === rowData.length - 1 checks that we are updating the data in the same row by pressing tab
       */
        if (rowData && index === -1 && params.node.rowIndex === rowData.length - 1) {
          const updatedRows = [...rowData];
          const editingRow = rowData[params.node.rowIndex];
          updatedRows[params.node.rowIndex] = {
            ...editingRow,
            ...params.node.data,
          };
          if (isClipboardAction) {
            onRowEdited({ ...updatedRows[params.node.rowIndex], [colId]: newValue });
          } else {
            onRowEdited({ ...updatedRows[params.node.rowIndex] });
          }
        }
        // while updating existing row
        else {
          if (isClipboardAction) {
            onRowEdited({ ...data, [colId]: newValue });
          } else {
            onRowEdited({ ...data });
          }
        }
      }
    },
    [metadataColumns, validationFns, onRowEdited, rowData, originalRowData, erroredRowIds, theme.colors.blue]
  );

  const contextMenuItems = useCallback(
    (params) => {
      return [
        'autoSizeAll',
        'separator',
        'cut',
        'copy',
        'copyWithHeaders',
        'copyWithGroupHeaders',
        'paste',
        {
          // custom item
          name: `Delete`,
          action: () => {
            if (onRowsDeleted) {
              onRowsDeleted([params.node.data.id]); //TODO: Revisit during multi row delete implementation
            }
          },
          icon: '<span class="ag-icon ag-icon-cross" style="color:#da291c;" role="presentation"></span>',
        },
        'separator',
        'export',
      ];
    },
    [onRowsDeleted]
  );

  const handleCellKeyDown = useCallback(
    (keyPressEvent: CellKeyDownEvent<T>) => {
      const { event, data, column, api, rowIndex, colDef } = keyPressEvent;
      //@ts-ignore event.key attribute is actually present but it is missing in the Ag-grid types
      if (event.ctrlKey && !event.shiftKey && event?.code === 'Period') {
        // stopping the cell editing, and then clicking on the picklist icon using DOM
        // otherwise the picklist will not work
        // Reason: while the cell is in editing mode, CustomCellEditor is getting rendered,
        // >>> once the picklist button is clicked, the picklist opens
        // >>> clicking anywhere will stop the cell from editing and it will cause the CustomCellEditor to unmount, hence the picklist will close
        // >>> Hence manually stopping the cell Edit and then clicking on the picklist icon again

        api.stopEditing();
        // Open picklist on ctrl + .
        setTimeout(() => {
          const focusedCell = api.getFocusedCell();
          if (focusedCell) {
            const { rowIndex, column } = focusedCell;
            // selecting the cell element
            const cellElement = document.querySelector(
              `.ag-row[row-index="${rowIndex}"] [col-id="${column.getColId()}"]`
            );
            // targeting the popperButton
            // "popperButton" is the class assigned to picklist icon.
            // triggering a click on the popperButton element to open the picklist
            const popperButton = cellElement?.querySelector('.popperButton');
            if (popperButton && colDef.editable) {
              (popperButton as HTMLElement).click();
            }
          }
        }, 100);
      }
      //@ts-ignore event.code attribute is actually present but it is missing in the Ag-grid types
      if (event?.code === 'Space') {
        const metadataCol = find(metadataColumns, (item) => item.memberName === column.getColId());
        // toggle checkbox if present
        if (onRowEdited && metadataCol?.dataType === DataType.BOOLEAN) {
          onRowEdited({ ...data, [column.getColId()]: !get(data, column.getColId()) } as T);
        }
      }
      //@ts-ignore event.key attribute is actually present but it is missing in the Ag-grid types
      if (event?.key === 'Delete') {
        const selectedNodes = gridRef?.current?.api.getSelectedNodes();
        if (selectedNodes && selectedNodes.length > 0 && onRowsDeleted && selectedRowIds) {
          onRowsDeleted(selectedRowIds);
          gridRef.current?.api.clearFocusedCell();
        }
      }
      //@ts-ignore event.key attribute is actually present but it is missing in the Ag-grid types
      if (event?.key === 'Enter') {
        const updatedColDef = filter(columnDefs, (item) => !item.hide && item.editable);
        const lastEditableColumn = last(updatedColDef) ? (last(updatedColDef) as ColDef).field : '';
        const isLastColumn = colDef.field === lastEditableColumn;
        const firstEditableColumn = first(updatedColDef) ? (first(updatedColDef) as ColDef).field : '';
        const currentColumnIndex = updatedColDef?.findIndex((col) => col.field === colDef.field);
        const nextEditableColumn = updatedColDef[currentColumnIndex + 1]?.field ?? '';
        api.stopEditing();
        // Edit the empty row on pressing arrow down/enter from last row if last row is not empty or there's only 1 column
        setTimeout(() => {
          (typeof rowIndex === 'number' || !isNil(rowIndex)) &&
            api.setFocusedCell(
              isLastColumn ? rowIndex + 1 : rowIndex,
              isLastColumn ? firstEditableColumn : nextEditableColumn
            );
        }, 200);
      }

      // @ts-ignore event.key attribute is actually present but it is missing in the Ag-grid types
      if (event?.key === 'Insert' && insertRow) {
        insertRow(rowIndex ?? 0);
      }
    },
    [columnDefs, insertRow, metadataColumns, onRowEdited, onRowsDeleted, selectedRowIds]
  );

  // TODO Test this if it works after the changes
  const onRowDragMove = useCallback(
    (event: RowDragMoveEvent) => {
      const movingNode = event.node;
      const overNode = event.overNode;

      const rowNeedsToMove = movingNode !== overNode;
      if (rowNeedsToMove) {
        // const gridRows = gridRef.current?.api.getrow
        // the list of rows we have is data, not row nodes, so extract the data
        const movingData = movingNode.data;
        const overData = overNode?.data;
        const fromIndex = rowData?.indexOf(movingData) || 0;
        const toIndex = (overNode?.data ? rowData?.indexOf(overData) : rowData?.indexOf(overData) || 0) || 0;
        if (toIndex < 0 || toIndex >= (rowData?.length || 0)) return; //To avoid dragging beyond the empty row
        if (onRowMoved) {
          //The calling component need to pass the arranged data back, If it not passed the rows will still be arranged but will have a flickering effect in the UI
          const arrangedRows = onRowMoved(fromIndex, toIndex);

          gridRef.current?.api.setGridOption('rowData', arrangedRows); //Hack to show smooth transition in the UI
          gridRef.current?.api.clearFocusedCell();
        }
      }
    },
    [onRowMoved, rowData]
  );

  const onSearchTextChanged = useCallback((searchVal: string) => {
    gridRef.current?.api.setGridOption('quickFilterText', searchVal);
  }, []);

  const onRowSelected = useCallback(() => {
    const selectedRows = gridRef.current?.api?.getSelectedRows();
    const selectedIds = filter(
      map(selectedRows, (selectedRow) => selectedRow.id),
      Boolean
    );
    setSelectedRowIds(selectedIds);
    if (onGridRowSelected) onGridRowSelected(selectedRows);
  }, [onGridRowSelected]);

  const handleIssue = useCallback((issuedCommodities) => {
    getIssuedCommodities(gridRef, issuedCommodities);
  }, []);

  const addHandler = () => {
    const firstEditableColumn = columnDefs?.find((item) => item.editable)?.colId ?? '';
    gridRef.current?.api.ensureIndexVisible(rowData?.length ?? 0, 'bottom');
    // Set timeout to ensure that scrolling has finished before editing
    setTimeout(() => {
      gridRef.current?.api.startEditingCell({
        // if no row data is present, startEditing the first row (0 index)
        rowIndex: rowData?.length ?? 0,
        colKey: firstEditableColumn,
      });
    }, 100);
  };

  const cloneHandler = () => {
    if (gridRef.current && gridRef.current.api.getSelectedRows()) {
      // clone button will only be enabled if 1 row is selected. So picking the 1st row object
      const selectedRow = gridRef.current.api.getSelectedRows()[0];
      const metadataCol = find(metadataColumns, (item) => item.memberName === uniqueIdentifier);
      // no need to applyTransaction here, since data can also be controlled by updating rowData
      // and parent component will have track of which rows are deleted.
      if (onRowEdited && metadataCol) {
        const newTag =
          metadataCol.columnWidth > 6
            ? `${get(selectedRow, uniqueIdentifier)}`.substring(0, metadataCol.columnWidth - 5) + '-COPY'
            : `${get(selectedRow, uniqueIdentifier)}`;
        const updatedRow = updateObjectByKey(selectedRow, uniqueIdentifier, newTag);
        onRowEdited({ ...updatedRow, id: crypto.randomUUID() });
      }
    }
  };

  const deleteHandler = () => {
    if (onRowsDeleted) {
      // no need to applyTransaction here, since data can also be controlled by updating rowData
      // and parent component will have track of which rows are deleted.
      onRowsDeleted(selectedRowIds);
    }
  };

  if (loading || networkStatus === NetworkStatus.refetch) {
    return (
      <Box flexGrow={{ sm: 1 }} alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
        <AppSpinner size={IconSize.MEDIUM} />
      </Box>
    );
  }

  if (!loading && error) {
    return <ErrorRetry refetch={refetch} />;
  }

  if (!loading && !error && data && (!data.tableMetadata || !data.tableMetadata?.metadataColumns?.length)) {
    return (
      <Box p={{ sm: '500' }} flexGrow={{ sm: 1 }}>
        <MultiLingualText size={400}>No metadata found for the component.</MultiLingualText>
      </Box>
    );
  }

  return (
    <>
      <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} style={{ height: '100%' }}>
        <Box px={{ sm: containerHorizontalSpacing }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
          {showHeader && (
            <ClientGridHeader
              onSearchTextChanged={(value) => onSearchTextChanged(value)}
              layoutVisibleFlag={layoutVisibleFlag}
              setLayoutVisibleFlag={setLayoutVisibleFlag}
              saveLayoutModal={saveLayoutModal}
              allLayouts={allLayouts}
              setSelectedLayoutId={setSelectedLayoutId}
              selectedLayout={selectedLayout}
              name={name}
              hideSaveLayout={hideSaveLayout}
              isChangeControl={isChangeControl}
              changeManagementId={selectedRowIds[0]}
              engineeringStatus={engineeringStatus ?? ''}
              changeManagementPermissions={changeManagementPermissions}
              changeManagementSignature={changeManagementSignature}
              onIssue={handleIssue}
              canAddItem={!readOnly && canAddItem}
              canCloneItem={!readOnly && canAddItem && selectedRowIds?.length === 1}
              canDeleteItem={!readOnly && canDeleteItem && selectedRowIds?.length > 0}
              addHandler={addHandler}
              cloneHandler={cloneHandler}
              deleteHandler={deleteHandler}
              hideSearch={hideSearch}
            />
          )}
          {!loading && !error && (
            <GridWrapperStyled fontSize={fontSize ?? DEFAULT_FONT_SIZE} mixedCaseFilter={mixedCaseFilter}>
              <TableMetadataContext metadataTableName={name} tableMetaData={tableMetaData}>
                <AgGridReact
                  getRowId={({ data }) => {
                    return data.id;
                  }}
                  columnDefs={gridColumnDefs}
                  rowData={rowData}
                  defaultColDef={defaultColumnDefs}
                  animateRows={true}
                  ref={gridRef}
                  cacheBlockSize={60}
                  rowMultiSelectWithClick={rowSelection === 'multiple' ? true : false}
                  rowSelection={rowSelection}
                  enableRangeSelection={true}
                  suppressBrowserResizeObserver={true} //To handle resize observer issue as per https://github.com/ag-grid/ag-grid/issues/6562
                  components={{
                    ...cellRendererComponents(navigationURL, onRowEdited, rowHeight ?? DEFAULT_ROW_HEIGHT),
                  }}
                  rowHeight={rowHeight ?? DEFAULT_ROW_HEIGHT}
                  onRowClicked={({ data }) => {
                    if (onClick) {
                      onClick(data);
                    }
                  }}
                  onRowDoubleClicked={({ data }) => {
                    if (onDoubleClick) {
                      onDoubleClick(data);
                    }
                  }}
                  onColumnVisible={onColumnVisibilityChanged}
                  headerHeight={headerHeight ? headerHeight : getHeaderHeight(columnDefs)}
                  onCellEditingStopped={onCellEditingStopped}
                  processCellFromClipboard={(params) => {
                    onCellEditingStopped(params);
                  }}
                  onCellKeyDown={handleCellKeyDown}
                  getContextMenuItems={contextMenuItems}
                  onRowDragMove={onRowDragMove}
                  stopEditingWhenCellsLoseFocus={true}
                  statusBar={showTotal ? statusBar : undefined}
                  suppressContextMenu={true}
                  enableCellChangeFlash={flash}
                  reactiveCustomComponents
                  autoSizeStrategy={autoSizeStrategy}
                  onRowSelected={onRowSelected}
                  onGridReady={handleGridReady}
                  suppressRowClickSelection={true}
                  onSortChanged={onSortChange ? onSortChange : onSortChanged}
                  onFilterChanged={onFilterChanged}
                  gridOptions={{
                    postSortRows(params) {
                      const rowNodes = params.nodes;
                      for (let i = 0; i < rowNodes?.length; i++) {
                        if (!rowNodes[i].data?.id) {
                          rowNodes.push(rowNodes.splice(i, 1)[0]);
                        }
                      }
                    },
                  }}
                  onDragStarted={onDragStarted}
                  sideBar={showSidebar ? sideBarData : false}
                  onDragStopped={onDragStopped}
                  suppressNoRowsOverlay={true}
                  rowDragManaged={rowDragManaged}
                ></AgGridReact>
              </TableMetadataContext>
            </GridWrapperStyled>
          )}
        </Box>
        {layoutModalVisible && (
          <LayoutModal
            selectedColumns={selectedColumns ? selectedColumns : displayedSelectClause}
            data={data}
            name={name}
            close={closeLayoutModal}
            description={selectedLayout.id === defaultLayout.id ? '' : selectedLayout.description}
            visible={layoutModalVisible}
            filters={filterModel}
            sortOrders={sortingOrder}
            onLayoutSaved={(userAccountLayout: UserAccountLayout) => {
              setLayoutVisibleFlag(false);
              onLayoutChanged(userAccountLayout);
              gridRef.current?.api?.setFilterModel(getFormattedFilters(userAccountLayout.whereClause));
            }}
          />
        )}
      </Box>
    </>
  );
};
